import React, { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  MessageCircle,
  X,
  Send,
  ChevronLeft,
  Plus,
  CheckCircle2,
  Paperclip,
  FileText,
  Image,
  Download,
  Trash2,
  Headphones,
  Lightbulb,
  LifeBuoy,
  MessageSquareHeart,
} from "lucide-react";
import api from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────

interface Attachment {
  _id?: string;
  fileName: string;
  fileKey: string;
  fileSize: number;
  mimeType: string;
}

interface SupportMessage {
  _id?: string;
  sender: "user" | "admin";
  text: string;
  attachments?: Attachment[];
  createdAt: string;
}

interface SupportTicket {
  _id: string;
  subject: string;
  status: "open" | "replied" | "resolved";
  messages: SupportMessage[];
  updatedAt: string;
  createdAt: string;
}

interface PendingFile {
  file: File;
  id: string;
  preview?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────

const STATUS_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  open: { bg: "bg-amber-50", text: "text-amber-700", label: "Open" },
  replied: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Replied" },
  resolved: { bg: "bg-slate-100", text: "text-slate-500", label: "Resolved" },
};

function timeAgo(date: string) {
  const s = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(mimeType: string) {
  return mimeType.startsWith("image/");
}

function getFileIcon(mimeType: string) {
  if (isImageType(mimeType)) return Image;
  return FileText;
}

// ─── Attachment Preview Chip ──────────────────────────────────────────

function PendingFileChip({
  pf,
  onRemove,
}: {
  pf: PendingFile;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-xl border border-slate-200 group">
      {pf.preview ? (
        <img
          src={pf.preview}
          alt=""
          className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-emerald-600" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-700 truncate">
          {pf.file.name}
        </p>
        <p className="text-[10px] text-slate-400">
          {formatFileSize(pf.file.size)}
        </p>
      </div>
      <button
        onClick={onRemove}
        className="p-1 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Sent Attachment Chip ─────────────────────────────────────────────

function AttachmentChip({
  att,
  isUser,
}: {
  att: Attachment;
  isUser: boolean;
}) {
  const Icon = getFileIcon(att.mimeType);

  const handleDownload = async () => {
    try {
      const res = await api.get(`/api/support/attachments/${att.fileKey}`);
      window.open(res.data.url, "_blank");
    } catch {}
  };

  return (
    <button
      onClick={handleDownload}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all mt-1.5 w-full ${
        isUser
          ? "bg-white/10 hover:bg-white/20"
          : "bg-white hover:bg-slate-50 border border-slate-200"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isUser ? "bg-white/15" : "bg-emerald-50"
        }`}
      >
        <Icon className={`w-4 h-4 ${isUser ? "text-white" : "text-emerald-600"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-xs font-medium truncate ${
            isUser ? "text-white" : "text-slate-700"
          }`}
        >
          {att.fileName}
        </p>
        <p
          className={`text-[10px] ${
            isUser ? "text-emerald-200" : "text-slate-400"
          }`}
        >
          {formatFileSize(att.fileSize)}
        </p>
      </div>
      <Download
        className={`w-3.5 h-3.5 flex-shrink-0 ${
          isUser ? "text-emerald-200" : "text-slate-400"
        }`}
      />
    </button>
  );
}

// ─── Main Widget ──────────────────────────────────────────────────────

export interface SupportChatWidgetProps {
  /** Hide the floating bubble (when another launcher, like the assistant, opens this widget). */
  hideLauncher?: boolean;
  /** Controlled open state. When provided, the widget does not manage its own open state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Notified whenever the unread reply count changes. Also enables background polling. */
  onUnreadChange?: (count: number) => void;
}

export default function SupportChatWidget({
  hideLauncher,
  open: controlledOpen,
  onOpenChange,
  onUnreadChange,
}: SupportChatWidgetProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? !!controlledOpen : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );
  const [view, setView] = useState<"list" | "new" | "thread">("list");
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(false);

  // New ticket form
  const [message, setMessage] = useState("");
  const [newFiles, setNewFiles] = useState<PendingFile[]>([]);
  const [sending, setSending] = useState(false);

  // Reply
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState<PendingFile[]>([]);
  const [replying, setReplying] = useState(false);

  // Drag state
  const [dragging, setDragging] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // ── Data fetching ───────────────────────────────────────────────────

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/api/support/tickets");
      const t = res.data.tickets || [];
      setTickets(t);
      setUnreadCount(
        t.filter((tk: SupportTicket) => tk.status === "replied").length
      );
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchTickets();
  }, [open, fetchTickets]);

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, [open, fetchTickets]);

  // Background unread polling, only when a parent asked to be told about unread replies
  const hasUnreadListener = !!onUnreadChange;
  useEffect(() => {
    if (!hasUnreadListener || open) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get("/api/support/tickets");
        if (cancelled) return;
        const t = res.data.tickets || [];
        setUnreadCount(
          t.filter((tk: SupportTicket) => tk.status === "replied").length
        );
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [hasUnreadListener, open]);

  useEffect(() => {
    onUnreadChange?.(unreadCount);
  }, [unreadCount, onUnreadChange]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeTicket?.messages]);

  // ── File handling ───────────────────────────────────────────────────

  const addFiles = useCallback(
    (
      fileList: FileList | File[],
      target: "new" | "reply"
    ) => {
      const files = Array.from(fileList);
      const pending: PendingFile[] = files.map((file) => {
        const pf: PendingFile = {
          file,
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        };
        if (file.type.startsWith("image/")) {
          pf.preview = URL.createObjectURL(file);
        }
        return pf;
      });

      if (target === "new") {
        setNewFiles((prev) => [...prev, ...pending].slice(0, 5));
      } else {
        setReplyFiles((prev) => [...prev, ...pending].slice(0, 5));
      }
    },
    []
  );

  const removeFile = (id: string, target: "new" | "reply") => {
    const setter = target === "new" ? setNewFiles : setReplyFiles;
    setter((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  // ── Drag & drop ─────────────────────────────────────────────────────

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files.length > 0) {
      const target = view === "new" ? "new" : "reply";
      addFiles(e.dataTransfer.files, target);
    }
  };

  // ── Create ticket ───────────────────────────────────────────────────

  const buildFormData = (
    text: string,
    files: PendingFile[]
  ): FormData => {
    const fd = new FormData();
    fd.append("message", text);
    files.forEach((pf) => fd.append("attachments", pf.file));
    return fd;
  };

  const handleCreateTicket = async () => {
    if (!message.trim() && newFiles.length === 0) return;
    setSending(true);
    try {
      const fd = buildFormData(message.trim(), newFiles);
      const res = await api.post("/api/support/tickets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Cleanup previews
      newFiles.forEach((pf) => pf.preview && URL.revokeObjectURL(pf.preview));
      setMessage("");
      setNewFiles([]);
      setActiveTicket(res.data);
      setView("thread");
      fetchTickets();
    } catch {
    } finally {
      setSending(false);
    }
  };

  // ── Reply ───────────────────────────────────────────────────────────

  const handleReply = async () => {
    if ((!reply.trim() && replyFiles.length === 0) || !activeTicket) return;
    setReplying(true);
    try {
      const fd = buildFormData(reply.trim(), replyFiles);
      const res = await api.post(
        `/api/support/tickets/${activeTicket._id}/messages`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      replyFiles.forEach((pf) => pf.preview && URL.revokeObjectURL(pf.preview));
      setReply("");
      setReplyFiles([]);
      setActiveTicket(res.data);
      fetchTickets();
    } catch {
    } finally {
      setReplying(false);
    }
  };

  const openTicket = async (ticket: SupportTicket) => {
    try {
      const res = await api.get(`/api/support/tickets/${ticket._id}`);
      setActiveTicket(res.data);
      setView("thread");
    } catch {}
  };

  // ── Render ──────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Floating Chat Bubble ────────────────────────────────────── */}
      {!hideLauncher && (
      <motion.button
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg, #0F5132 0%, #198754 100%)",
          boxShadow: "0 8px 32px rgba(15,81,50,0.35)",
        }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <MessageCircle className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
        {!open && unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5.5 h-5.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white"
          >
            {unreadCount}
          </motion.span>
        )}
      </motion.button>
      )}

      {/* ── Chat Panel ──────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.92 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="fixed z-50 bg-white flex flex-col overflow-hidden inset-0 sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[400px] sm:rounded-2xl sm:max-h-[min(680px,calc(100vh-120px))] sm:border sm:border-slate-200/80 sm:shadow-[0_25px_65px_rgba(0,0,0,0.15),0_8px_20px_rgba(0,0,0,0.08)]"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Drag overlay */}
            <AnimatePresence>
              {dragging && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 z-30 bg-emerald-50/95 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-emerald-400"
                >
                  <Paperclip className="w-10 h-10 text-emerald-500 mb-3" />
                  <p className="text-sm font-bold text-emerald-700">
                    Drop files here
                  </p>
                  <p className="text-xs text-emerald-500 mt-1">
                    Up to 5 files, 10 MB each
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Header ───────────────────────────────────────────── */}
            <div
              className="px-5 py-4 flex items-center gap-3 pt-[max(1rem,env(safe-area-inset-top))]"
              style={{
                background: "linear-gradient(135deg, #0F5132 0%, #146C43 100%)",
              }}
            >
              {view !== "list" && (
                <button
                  onClick={() => {
                    setView("list");
                    setActiveTicket(null);
                    setReplyFiles([]);
                  }}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-[15px] truncate">
                  {view === "list"
                    ? "Chat with us"
                    : view === "new"
                    ? "New Message"
                    : "Conversation"}
                </h3>
                <p className="text-emerald-200/80 text-xs">
                  {view === "list"
                    ? "We typically reply within a few hours"
                    : view === "new"
                    ? "We'd love to hear from you"
                    : `#${activeTicket?._id.slice(-6)} · ${
                        STATUS_STYLE[activeTicket?.status || "open"]?.label
                      }`}
                </p>
              </div>
              {view === "list" && (
                <button
                  onClick={() => setView("new")}
                  className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-all"
                  title="New conversation"
                >
                  <Plus className="w-4.5 h-4.5" />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors sm:hidden"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ── Ticket List ──────────────────────────────────────── */}
            {view === "list" && (
              <div className="flex-1 overflow-y-auto">
                {loading && tickets.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="inline-block w-7 h-7 border-2 border-slate-200 border-t-[#0F5132] rounded-full animate-spin" />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                      <Headphones className="w-7 h-7 text-emerald-400" />
                    </div>
                    <p className="text-[15px] font-bold text-slate-800 mb-1">
                      We're here for you
                    </p>
                    <p className="text-xs text-slate-400 mb-5 max-w-[260px] mx-auto leading-relaxed">
                      Your feedback helps us build a better product for your firm.
                    </p>

                    <div className="space-y-2.5 mb-6 text-left max-w-[280px] mx-auto">
                      <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-amber-50/70">
                        <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Lightbulb className="w-3.5 h-3.5 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">Request a feature</p>
                          <p className="text-[11px] text-slate-400 leading-relaxed">Have an idea? We'll build it for you.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-blue-50/70">
                        <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <LifeBuoy className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">Get help</p>
                          <p className="text-[11px] text-slate-400 leading-relaxed">Stuck on something? We're happy to help.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-purple-50/70">
                        <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <MessageSquareHeart className="w-3.5 h-3.5 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-700">Share feedback</p>
                          <p className="text-[11px] text-slate-400 leading-relaxed">Love it or hate it, we want to know.</p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setView("new")}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0F5132] text-white text-sm font-bold hover:bg-[#146C43] transition-all shadow-sm"
                    >
                      <Send className="w-4 h-4" />
                      Send us a message
                    </button>
                  </div>
                ) : (
                  <div>
                    {tickets.map((ticket, idx) => {
                      const ss = STATUS_STYLE[ticket.status];
                      const lastMsg =
                        ticket.messages[ticket.messages.length - 1];
                      const hasUnread = ticket.status === "replied";
                      return (
                        <button
                          key={ticket._id}
                          onClick={() => openTicket(ticket)}
                          className={`w-full px-5 py-4 text-left hover:bg-slate-50 transition-colors ${
                            idx < tickets.length - 1
                              ? "border-b border-slate-100"
                              : ""
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              {hasUnread && (
                                <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-slate-900 truncate">
                                {lastMsg?.sender === "admin"
                                  ? "Kavoa Support"
                                  : "You"}
                              </span>
                              <span className="text-[10px] text-slate-400 flex-shrink-0">
                                {timeAgo(ticket.updatedAt)}
                              </span>
                            </div>
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-2 ${ss.bg} ${ss.text}`}
                            >
                              {ss.label}
                            </span>
                          </div>
                          <p className="text-[13px] text-slate-600 truncate leading-relaxed">
                            {lastMsg?.text?.slice(0, 80) || "Sent an attachment"}
                            {(lastMsg?.text?.length || 0) > 80 ? "..." : ""}
                          </p>
                        </button>
                      );
                    })}

                    {/* New conversation footer button */}
                    <div className="p-3 border-t border-slate-100">
                      <button
                        onClick={() => setView("new")}
                        className="w-full py-2.5 rounded-xl border border-dashed border-slate-300 text-slate-500 text-xs font-semibold hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all flex items-center justify-center gap-1.5"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        New Conversation
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── New Message ─────────────────────────────────────── */}
            {view === "new" && (
              <div className="flex-1 flex flex-col">
                {/* Prompt area */}
                <div className="flex-1 overflow-y-auto px-5 pt-5 pb-3">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-[#0F5132] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Headphones className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 text-sm text-slate-700 leading-relaxed">
                      <p className="font-semibold text-slate-800 mb-1">Hey there!</p>
                      <p className="text-slate-500 text-[13px]">
                        Tell us what's on your mind. Request a feature, ask for help, or share feedback. We read every message.
                      </p>
                    </div>
                  </div>

                  {/* Pending files */}
                  {newFiles.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {newFiles.map((pf) => (
                        <PendingFileChip
                          key={pf.id}
                          pf={pf}
                          onRemove={() => removeFile(pf.id, "new")}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Chat-style input bar */}
                <div className="border-t border-slate-100">
                  <div className="px-4 py-3 flex items-end gap-2">
                    <button
                      onClick={() => newFileInputRef.current?.click()}
                      className="p-2.5 rounded-xl text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex-shrink-0"
                      title="Attach files"
                    >
                      <Paperclip className="w-4.5 h-4.5" />
                    </button>
                    <input
                      ref={newFileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) addFiles(e.target.files, "new");
                        e.target.value = "";
                      }}
                    />
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleCreateTicket();
                        }
                      }}
                      placeholder="Type a message..."
                      rows={1}
                      className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 resize-none transition-all max-h-[100px]"
                      style={{ minHeight: "42px" }}
                    />
                    <button
                      onClick={handleCreateTicket}
                      disabled={
                        sending ||
                        (!message.trim() && newFiles.length === 0)
                      }
                      className="p-2.5 rounded-xl bg-[#0F5132] hover:bg-[#146C43] text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      {sending ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Thread View ──────────────────────────────────────── */}
            {view === "thread" && activeTicket && (
              <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                  {/* Welcome system message */}
                  <div className="flex justify-center mb-2">
                    <span className="text-[10px] text-slate-400 bg-slate-50 px-3 py-1 rounded-full">
                      Conversation started{" "}
                      {new Date(activeTicket.createdAt).toLocaleDateString(
                        "en-US",
                        { month: "short", day: "numeric" }
                      )}
                    </span>
                  </div>

                  {activeTicket.messages.map((msg, i) => {
                    const isUser = msg.sender === "user";
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className={`flex ${
                          isUser ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div className="max-w-[82%]">
                          {!isUser && (
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-5 h-5 rounded-full bg-[#0F5132] flex items-center justify-center">
                                <Headphones className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-[10px] font-bold text-slate-500">
                                Kavoa Support
                              </span>
                            </div>
                          )}
                          <div
                            className={`px-4 py-3 text-sm leading-relaxed ${
                              isUser
                                ? "bg-[#0F5132] text-white rounded-2xl rounded-br-md"
                                : "bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md"
                            }`}
                          >
                            {msg.text && (
                              <p className="whitespace-pre-wrap">{msg.text}</p>
                            )}
                            {msg.attachments &&
                              msg.attachments.length > 0 && (
                                <div className={msg.text ? "mt-2" : ""}>
                                  {msg.attachments.map((att, j) => (
                                    <AttachmentChip
                                      key={j}
                                      att={att}
                                      isUser={isUser}
                                    />
                                  ))}
                                </div>
                              )}
                          </div>
                          <p
                            className={`text-[10px] mt-1 px-1 ${
                              isUser ? "text-right text-slate-400" : "text-slate-400"
                            }`}
                          >
                            {timeAgo(msg.createdAt)}
                          </p>
                        </div>
                      </motion.div>
                    );
                  })}

                  {activeTicket.status === "resolved" && (
                    <div className="flex justify-center py-2">
                      <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-xs font-semibold">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Resolved
                      </span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply area */}
                {activeTicket.status !== "resolved" && (
                  <div className="border-t border-slate-100">
                    {/* Pending reply files */}
                    {replyFiles.length > 0 && (
                      <div className="px-4 pt-3 space-y-1.5">
                        {replyFiles.map((pf) => (
                          <PendingFileChip
                            key={pf.id}
                            pf={pf}
                            onRemove={() => removeFile(pf.id, "reply")}
                          />
                        ))}
                      </div>
                    )}
                    <div className="px-4 py-3 flex items-end gap-2">
                      <button
                        onClick={() => replyFileInputRef.current?.click()}
                        className="p-2.5 rounded-xl text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all flex-shrink-0"
                        title="Attach files"
                      >
                        <Paperclip className="w-4.5 h-4.5" />
                      </button>
                      <input
                        ref={replyFileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files)
                            addFiles(e.target.files, "reply");
                          e.target.value = "";
                        }}
                      />
                      <input
                        type="text"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleReply();
                          }
                        }}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition-all"
                      />
                      <button
                        onClick={handleReply}
                        disabled={
                          replying ||
                          (!reply.trim() && replyFiles.length === 0)
                        }
                        className="p-2.5 rounded-xl bg-[#0F5132] hover:bg-[#146C43] text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        {replying ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
